import { useEffect, useRef } from 'react'

/**
 * useScrollAnimation
 * Attaches an IntersectionObserver to elements with [data-animate].
 * Adds .visible class when they enter the viewport, triggering CSS transitions.
 */
export function useScrollAnimation() {
  useEffect(() => {
    const targets = document.querySelectorAll('[data-animate]')

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = entry.target.dataset.delay ? Number(entry.target.dataset.delay) : 0
            setTimeout(() => {
              entry.target.classList.add('visible')
            }, delay)
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    )

    targets.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [])
}

/**
 * useAnimateOnMount
 * Returns a ref — attach to any element to animate it on mount.
 */
export function useAnimateOnMount(delay = 0) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const timer = setTimeout(() => {
      el.classList.add('visible')
    }, delay)
    return () => clearTimeout(timer)
  }, [delay])

  return ref
}

export default useScrollAnimation
